import type { ModalComponentProps } from '@components/modals';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { dismissNotification } from '../store/slice';
import type { SystemNotification } from '../types';

/**
 * System Notification Modal
 *
 * NOTE: This component does NOT include a Dialog wrapper.
 * The GlobalModalManager handles the Dialog container.
 * We only return the content (DialogTitle, DialogContent, DialogActions).
 */
export const SystemNotificationModal = ({ modalProps, closeModal }: ModalComponentProps) => {
  const dispatch = useAppDispatch();
  const { systemNotification } = useEventTracking();
  const notification = modalProps.notification as SystemNotification;

  const handleDismiss = () => {
    systemNotification.modalDismissed({
      notification_id: notification.id,
      notification_type: 'modal',
      severity: notification.severity,
      dismissal_type: notification.persistent ? 'persistent' : 'session',
    });

    dispatch(
      dismissNotification({
        id: notification.id,
        persistent: notification.persistent,
      }),
    );
    closeModal();
  };

  const handleLinkClick = () => {
    if (notification.link) {
      systemNotification.linkClicked({
        notification_id: notification.id,
        notification_type: 'modal',
        severity: notification.severity,
        link_url: notification.link.url,
        link_text: notification.link.text,
      });
    }
  };

  const getSeverityColor = (severity: SystemNotification['severity']) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  return (
    <>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span" color={getSeverityColor(notification.severity)}>
            {notification.title}
          </Typography>
          {notification.dismissible && (
            <IconButton aria-label="close" onClick={handleDismiss} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" paragraph>
          {notification.message}
        </Typography>
        {notification.link && (
          <Box sx={{ mt: 2 }}>
            <Link
              href={notification.link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              {notification.link.text}
            </Link>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDismiss}
          variant="contained"
          color={getSeverityColor(notification.severity)}
        >
          {notification.dismissible ? 'Dismiss' : 'Close'}
        </Button>
      </DialogActions>
    </>
  );
};

export default SystemNotificationModal;
